import React, { FC, useEffect, useState } from 'react'
import { Box, makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { Scalars } from 'types/graphqlTypes'
import { StringParam } from 'use-query-params'
import { useNotesUpdate } from 'apps/marketplace/components/utils/hooks/useNotesAndIssuesUpdate'
import { useOpenModalHandler } from 'apps/marketplace/pages/shipment-details/hooks/useNewIssueModal'
import { Button } from 'transfix-ui'
import { log } from 'utils/errorLogger'
import { useQueryParams } from 'utils/hooks'
import { scrollToIssue } from '../../utils/scrollToIssue'
import { ErrorInlineMessage } from '../ErrorInlineMessage'
import { SectionErrorBoundary } from '../SectionErrorBoundary'
import { IssueRows, IssueRowHeaders } from './IssueRows'
import { useShipmentIssuesQuery } from './graphql/ShipmentIssues'

interface IShipmentIssuesProps {
  shipmentId: string
  readOnly?: boolean
}

interface IHasUpdatedAt {
  updatedAt: Scalars['ISO8601DateTime']
}

const comparator = <T extends IHasUpdatedAt>(newValue: T, previousValue: T): boolean =>
  newValue?.updatedAt === previousValue?.updatedAt

const useStyles = makeStyles(theme => ({
  row: {
    position: 'relative',
  },
  emptyIssues: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: brandColors.white,
    padding: theme.spacing(5),
    minHeight: '200px',
  },
  refresh: {
    marginLeft: theme.spacing(2),
  },
}))

const ShipmentIssues: FC<IShipmentIssuesProps> = ({ readOnly = false, shipmentId }) => {
  const classes = useStyles()
  const { data, error, loading, previousData } = useShipmentIssuesQuery({
    variables: {
      id: shipmentId,
    },
    pollInterval: 60000,
  })
  const [{ issueId }] = useQueryParams({
    issueId: StringParam,
  })

  const { openModalHandler } = useOpenModalHandler()

  const [activeIssue, setActiveIssue] = useState<string | null>(issueId)

  /**
   * Checks when the issueId query param change and update the variable activeIssue
   */
  useEffect(() => {
    if (issueId !== activeIssue) {
      setActiveIssue(issueId)
    }
    setTimeout(() => {
      scrollToIssue(issueId)
    }, 1000)
  }, [issueId, activeIssue])

  const { issues = [] } = data?.shipment || {}
  const { data: displayedIssues = [], refreshHandler, updateAvailable } = useNotesUpdate({
    loading,
    previousData: previousData?.shipment?.issues,
    newData: issues,
    comparator,
    type: 'issues',
    hasErrors: Boolean(error),
  })

  if (error) {
    log('rogers', error, { section: 'ShipmentIssues' })

    return <ErrorInlineMessage />
  }

  return loading ? null : (
    <SectionErrorBoundary section='ShipmentIssues'>
      <Box display='flex' flexBasis='70%' flexDirection='column'>
        <Box pb={1}>
          <Box display='flex' justifyContent='space-between'>
            <Box display='flex' justifyContent='space-between'>
              {!readOnly && <Typography variant='h3'>Issues</Typography>}
              {updateAvailable && (
                <Button color='secondary' onClick={refreshHandler} className={classes.refresh}>
                  Refresh Issues
                </Button>
              )}
            </Box>
            {!readOnly && (
              <Button color='secondary' data-test='log-new-issue-button' onClick={openModalHandler}>
                Log New Issue
              </Button>
            )}
          </Box>
        </Box>
        <Box pt={2} pb={2}>
          <IssueRowHeaders reducedVersion />
        </Box>
        <IssueRows
          issues={displayedIssues}
          shipmentId={shipmentId}
          activeIssue={activeIssue}
          reducedVersion
          readOnly={readOnly}
        />
        {displayedIssues.length === 0 && (
          <div className={classes.emptyIssues}>
            <Typography variant='subtitle1' color='textSecondary'>
              No issues have been logged
            </Typography>
          </div>
        )}
      </Box>
    </SectionErrorBoundary>
  )
}

export default ShipmentIssues
